import { ButtonBase, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Edit, ExpandMore, Refresh } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useRocInternalPortalStore } from '../../../hooks';
import { UnderwriterWriteupRun, Writeup, WriteupRun, WriteupSection } from '../types';

const REFRESH_MIN_MS = 1200;

const COLORS = {
  bg: '#F3F5F8',
  ink: '#16202B',
  ink2: '#3E4C59',
  mute: '#5A6875',
  line: '#DCE2E8',
  soft: '#F6F8FA',
  soft2: '#EEF1F4',
  blue: '#1F5FAD',
  blueDark: '#143F75',
  blueBg: '#F1F6FC',
  red: '#A0281C',
  redBg: '#FDECEA',
  green: '#1B6B42',
  greenBg: '#E3F3EA',
};

const SYNOPSIS = 'Synopsis';
const PROPERTY_HEADINGS = ['Collateral', 'Market', 'Surrounding'];
const BORROWER_HEADING = 'Borrower';
export const CREDIT_MEMO_HEIGHT_ACTION = 'credit-memo-height';
export const CREDIT_MEMO_READY_ACTION = 'credit-memo-ready';

type ParentKey = 'loan' | 'borrower' | 'property';
type Drafts = string[];

const useStyles = makeStyles(() => ({
  root: {
    alignSelf: 'flex-start',
    width: '100%',
    color: COLORS.ink,
    fontSize: 14,
    fontVariantNumeric: 'tabular-nums',
  },
  memo: {
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.line}`,
    borderRadius: 12,
    overflow: 'hidden',
  },
  memoHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingRight: 18,
    '&:hover': {
      backgroundColor: COLORS.soft,
    },
  },
  memoToggle: {
    flex: 1,
    minWidth: 0,
    minHeight: 60,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    textAlign: 'left',
  },
  memoTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
  },
  sub: {
    color: COLORS.mute,
    fontSize: 13,
  },
  date: {
    backgroundColor: COLORS.blueBg,
    border: '1px solid #BFD6EF',
    borderRadius: 4,
    color: COLORS.blueDark,
    fontWeight: 600,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  button: {
    alignItems: 'center',
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.blue}`,
    borderRadius: 6,
    color: COLORS.blue,
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    gap: 6,
    minHeight: 32,
    padding: '0 10px',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#EEF4FB',
    },
  },
  primary: {
    backgroundColor: COLORS.blue,
    color: '#fff',
    '&:hover': {
      backgroundColor: '#174C8C',
    },
  },
  spinning: {
    animation: '$spin 1s linear infinite',
  },
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
  ghost: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    color: COLORS.ink2,
    '&:hover': {
      backgroundColor: COLORS.soft2,
    },
  },
  body: {
    backgroundColor: COLORS.bg,
    borderTop: `1px solid ${COLORS.line}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '16px 20px 20px',
  },
  synopsisCard: {
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.line}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: '18px 20px 20px',
  },
  synopsisTitle: {
    margin: 0,
    fontSize: 19,
    fontWeight: 600,
  },
  subSections: {
    borderTop: `1px solid ${COLORS.line}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingTop: 16,
  },
  parent: {
    backgroundColor: '#fff',
    border: `1px solid ${COLORS.line}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  parentHeader: {
    width: '100%',
    minHeight: 56,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: COLORS.soft,
    },
  },
  parentTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 600,
  },
  chevron: {
    color: COLORS.ink2,
    transition: 'transform .15s',
  },
  collapsed: {
    transform: 'rotate(-90deg)',
  },
  panel: {
    borderTop: `1px solid ${COLORS.line}`,
    padding: '0 18px',
  },
  part: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '18px 0',
    '& + &': {
      borderTop: `1px solid ${COLORS.line}`,
    },
  },
  viewLink: {
    color: COLORS.blue,
    fontWeight: 600,
    marginLeft: 8,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: COLORS.blueDark,
    },
  },
  partTitle: {
    color: COLORS.ink,
    fontSize: 15,
    fontWeight: 600,
  },
  label: {
    color: COLORS.mute,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
  },
  pill: {
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    padding: '2px 10px',
    whiteSpace: 'nowrap',
  },
  red: {
    color: '#C00000',
    fontWeight: 700,
  },
  flag: {
    backgroundColor: COLORS.redBg,
    borderRadius: 8,
    color: COLORS.red,
    fontWeight: 700,
    padding: '10px 14px',
  },
  synopsisChip: {
    backgroundColor: COLORS.soft2,
    borderRadius: 4,
    fontWeight: 600,
    marginRight: 2,
    padding: '1px 6px',
  },
  list: {
    margin: 0,
    paddingLeft: 18,
    lineHeight: 1.55,
    '& li': {
      marginBottom: 4,
    },
  },
  paragraph: {
    margin: 0,
    lineHeight: 1.6,
  },
  synopsisText: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.6,
  },
  textarea: {
    width: '100%',
    font: 'inherit',
    fontSize: 14,
    lineHeight: 1.6,
    color: COLORS.ink,
    border: `1px solid ${COLORS.blue}`,
    borderRadius: 6,
    padding: '10px 12px',
    resize: 'vertical',
  },
}));

const formatDate = (date: string) =>
  date ? new Date(date).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const LINK_SITES: [string, string][] = [
  ['homegenius', 'HomeGenius'],
  ['redfin.com', 'Redfin'],
  ['coldwellbanker', 'Coldwell Banker'],
  ['zillow.com', 'Zillow'],
];

const linkText = (url: string) => {
  const site = LINK_SITES.find(([domain]) => url.includes(domain));
  return site ? `View in ${site[1]} ↗` : 'View here ↗';
};

// The comp's status ("SOLD $555K", "LISTED $499K") in bold, as the underwriters write it.
const LinkLabel = ({ label }: { label: string }) => (
  <>
    {label.split(/\b(SOLD|LISTED)\b/).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part))}
  </>
);

const runsText = (runs: WriteupRun[]) => runs.map(run => run.text).join('');

const linesOf = (text: string) => text.split('\n').map(line => line.trim()).filter(Boolean);

const isBulletSection = (section: WriteupSection) => section.bullets?.length > 0 || !section.paragraphs?.length;

const sectionText = (section: WriteupSection) =>
  isBulletSection(section) ? section.bullets.map(runsText).join('\n') : section.paragraphs.join('\n');

const withText = (section: WriteupSection, text: string): WriteupSection => {
  if (!isBulletSection(section)) {
    return { ...section, paragraphs: linesOf(text) };
  }
  const runsByText = new Map(section.bullets.map(runs => [runsText(runs), runs]));
  return { ...section, bullets: linesOf(text).map(line => runsByText.get(line) || [{ text: line }]) };
};

const startsWithAny = (heading: string, prefixes: string[]) => prefixes.some(prefix => heading.startsWith(prefix));

const parentOf = (heading: string): ParentKey =>
  startsWithAny(heading, PROPERTY_HEADINGS) ? 'property' : heading.startsWith(BORROWER_HEADING) ? 'borrower' : 'loan';

const firstClause = (section: WriteupSection) => {
  const text = section?.bullets?.length ? runsText(section.bullets[0]) : section?.paragraphs?.[0] || '';
  return text.split(':')[0].slice(0, 90);
};

const useReportedHeight = (ref: RefObject<HTMLElement>) => {
  useEffect(() => {
    const report = () => window.parent.postMessage({ action: CREDIT_MEMO_HEIGHT_ACTION, height: ref.current?.offsetHeight || 0 }, '*');
    const observer = new MutationObserver(report);
    observer.observe(ref.current, { attributes: true, characterData: true, childList: true, subtree: true });
    window.addEventListener('resize', report);
    report();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', report);
    };
  }, []);
};

const Runs = ({ runs, synopsis }: { runs: WriteupRun[]; synopsis?: boolean }) => {
  const classes = useStyles();
  return (
    <>
      {runs.map((run, i) => {
        const chip = synopsis && i === 0 ? run.text.match(/^(\d+\/\d+\/\d+)([\s\S]*)$/) : null;
        return (
          <span key={i} className={run.red ? classes.red : undefined} style={{ fontWeight: run.bold ? 700 : undefined }}>
            {chip ? <><span className={classes.synopsisChip} title="Initial LTC / Holdback / ARLTV">{chip[1]}</span>{chip[2]}</> : run.text}
          </span>
        );
      })}
    </>
  );
};

const SectionView = ({ section }: { section: WriteupSection }) => {
  const classes = useStyles();
  return (
    <>
      {section.paragraphs?.map((paragraph, i) => <p key={i} className={classes.paragraph}>{paragraph}</p>)}
      {!section.paragraphs?.length && !section.bullets?.length && !section.links?.length && <span className={classes.sub}>None</span>}
      {section.bullets?.length > 0 && (
        <ul className={classes.list}>
          {section.bullets.map((runs, i) => <li key={i}><Runs runs={runs} /></li>)}
        </ul>
      )}
      {section.links?.length > 0 && (
        <ol className={classes.list}>
          {section.links.map((link, i) => (
            <li key={i}>
              <LinkLabel label={link.label} />
              <Link className={classes.viewLink} href={link.url} target="_blank" rel="noopener noreferrer">{linkText(link.url)}</Link>
            </li>
          ))}
        </ol>
      )}
    </>
  );
};

const SectionText = ({ value, onChange }: { value: string; onChange: (text: string) => void }) => {
  const classes = useStyles();
  return (
    <textarea
      className={classes.textarea}
      rows={Math.max(3, value.split('\n').length + 1)}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  );
};

const Part = ({ label, titled, children }: { label: string; titled?: boolean; children: ReactNode }) => {
  const classes = useStyles();
  return (
    <div className={classes.part}>
      <span className={titled ? classes.partTitle : classes.label}>{label}</span>
      {children}
    </div>
  );
};

const Accordion = ({ title, sub, pill, open, onToggle, children }: {
  title: string; sub?: string; pill?: ReactNode; open: boolean; onToggle: () => void; children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <section className={classes.parent}>
      <ButtonBase className={classes.parentHeader} aria-expanded={open} onClick={onToggle}>
        <ExpandMore className={`${classes.chevron} ${open ? '' : classes.collapsed}`} />
        <h2 className={classes.parentTitle}>{title}</h2>
        {pill}
        {sub && <span className={classes.sub}>{sub}</span>}
      </ButtonBase>
      {open && <div className={classes.panel}>{children}</div>}
    </section>
  );
};

const MemoContent = ({ writeup, drafts, onDraft }: { writeup: Writeup; drafts: Drafts; onDraft: (index: number, text: string) => void }) => {
  const classes = useStyles();
  const editing = drafts !== null;
  const [open, setOpen] = useState<Record<ParentKey, boolean>>({ loan: true, borrower: false, property: false });
  const isOpen = (parent: ParentKey) => editing || open[parent];
  const toggle = (parent: ParentKey) => setOpen({ ...open, [parent]: !open[parent] });
  const indexed = writeup.sections.map((section, index) => ({ section, index }));
  const synopsis = indexed.find(({ section }) => section.heading === SYNOPSIS);
  const parts = (parent: ParentKey) =>
    indexed
      .filter(({ section }) => section.heading !== SYNOPSIS && parentOf(section.heading) === parent)
      .map(({ section, index }) => (
        <Part key={index} label={section.heading} titled={parent !== 'loan'}>
          {editing ? <SectionText value={drafts[index]} onChange={text => onDraft(index, text)} /> : <SectionView section={section} />}
        </Part>
      ));
  const borrowerCount = writeup.sections.filter(section => parentOf(section.heading) === 'borrower').length;
  const collaterals = writeup.sections.filter(section => section.heading.startsWith('Collateral'));

  return (
    <div className={classes.synopsisCard}>
      {synopsis && <h2 className={classes.synopsisTitle}>{SYNOPSIS}</h2>}
      {writeup.flag && <div className={classes.flag}>{writeup.flag}</div>}
      {synopsis && (editing ? (
        <SectionText value={drafts[synopsis.index]} onChange={text => onDraft(synopsis.index, text)} />
      ) : (
        <div className={classes.synopsisText}>
          {synopsis.section.paragraphs?.map((paragraph, i) => <p key={i} className={classes.synopsisText}>{paragraph}</p>)}
          {synopsis.section.bullets?.map((runs, i) => <p key={i} className={classes.synopsisText}><Runs runs={runs} synopsis /></p>)}
        </div>
      ))}
      <div className={classes.subSections} style={synopsis ? undefined : { borderTop: 0, paddingTop: 0 }}>
        <Accordion title="Loan" sub={writeup.subtitle} open={isOpen('loan')} onToggle={() => toggle('loan')}>
          {parts('loan')}
        </Accordion>
        <Accordion
          title="Borrower"
          sub={borrowerCount > 1 ? `${borrowerCount} borrowers` : undefined}
          open={isOpen('borrower')}
          onToggle={() => toggle('borrower')}
        >
          {parts('borrower')}
        </Accordion>
        <Accordion
          title="Property"
          sub={collaterals.length > 1 ? `${collaterals.length} properties` : firstClause(collaterals[0])}
          open={isOpen('property')}
          onToggle={() => toggle('property')}
        >
          {parts('property')}
        </Accordion>
      </div>
    </div>
  );
};

const HeaderStatus = ({ run }: { run: UnderwriterWriteupRun }) => {
  const classes = useStyles();
  if (!run) return <span className={classes.sub}>Not generated yet</span>;
  if (run.status === 'RUNNING') return <span className={classes.sub}>Generating since {formatDate(run.started_date)} · you can keep working</span>;
  if (run.status === 'FAILED') return <span className={classes.sub} style={{ color: COLORS.red }} title={run.error_message}>The credit memo could not be generated</span>;
  return (
    <>
      <span className={classes.sub}>
        Generated on: <span className={classes.date}>{formatDate(run.completed_date || run.started_date)}</span>
        {' · '}Last updated on: <span className={classes.date}>{formatDate(run.edited_date || run.completed_date || run.started_date)}</span>
      </span>
      {run.edited && <span className={classes.pill} style={{ backgroundColor: COLORS.soft2, color: COLORS.ink2 }}>Edited</span>}
      {run.submitted_date && (
        <span className={classes.pill} style={{ backgroundColor: COLORS.greenBg, color: COLORS.green }}>
          Submitted to committee · {formatDate(run.submitted_date)}
        </span>
      )}
    </>
  );
};

export const CreditMemo = observer(() => {
  const classes = useStyles();
  const { globalStore, underwriterWriteupStore } = useRocInternalPortalStore();
  const loanId = Number(globalStore.queryParams.loanId);
  const { access, run, isRunning } = underwriterWriteupStore;
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Drafts>(null);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const writeup = run?.status === 'SUCCESS' ? run.content : null;
  const canEdit = access?.can_edit;
  const visible = access?.can_view;

  const [loaded, setLoaded] = useState(false);

  useReportedHeight(rootRef);

  useEffect(() => {
    setLoaded(false);
    Promise.all([underwriterWriteupStore.fetchAccess(), underwriterWriteupStore.fetchLatestWriteup(loanId)]).then(() => setLoaded(true));
    return () => underwriterWriteupStore.stopPolling();
  }, [loanId]);

  useEffect(() => {
    if (loaded) {
      window.parent.postMessage({ action: CREDIT_MEMO_READY_ACTION, height: rootRef.current?.offsetHeight || 0 }, '*');
    }
  }, [loaded]);

  const generate = () => {
    setConfirmingRegenerate(false);
    underwriterWriteupStore.underwriteLoan(loanId);
  };
  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    Promise.all([underwriterWriteupStore.fetchLatestWriteup(loanId), new Promise(resolve => setTimeout(resolve, REFRESH_MIN_MS))]).finally(() => setRefreshing(false));
  };
  const startEditing = () => {
    setDrafts(writeup.sections.map(sectionText));
    setOpen(true);
  };
  const save = () => {
    underwriterWriteupStore.saveEditedContent({ ...writeup, sections: writeup.sections.map((section, index) => withText(section, drafts[index])) });
    setDrafts(null);
  };
  const button = (label: ReactNode, onClick: () => void, variant?: 'primary' | 'ghost') => (
    <button type="button" className={`${classes.button} ${variant ? classes[variant] : ''}`} onClick={onClick}>
      {label}
    </button>
  );

  const actions = () => {
    if (!canEdit) return null;
    if (drafts) return (
      <>
        <span className={classes.sub} style={{ color: COLORS.ink2 }}>Editing the memo</span>
        {button('Save', save, 'primary')}
        {button('Cancel', () => setDrafts(null), 'ghost')}
      </>
    );
    if (confirmingRegenerate) return (
      <>
        <span className={classes.sub} style={{ color: COLORS.ink2 }}>Regenerate? It replaces the memo and its edits.</span>
        {button('Regenerate', generate, 'primary')}
        {button('Cancel', () => setConfirmingRegenerate(false), 'ghost')}
      </>
    );
    if (isRunning) return button(<><Refresh fontSize="small" className={refreshing ? classes.spinning : undefined} />{refreshing ? 'Refreshing…' : 'Refresh'}</>, refresh);
    if (!writeup) return button('Generate credit memo', generate, 'primary');
    return (
      <>
        {!run.submitted_date && button(<><Edit fontSize="small" />Edit</>, startEditing)}
        {button(<><Refresh fontSize="small" />Regenerate</>, () => setConfirmingRegenerate(true))}
      </>
    );
  };

  return (
    <div ref={rootRef} className={classes.root}>
      {visible && (
        <section className={classes.memo}>
          <div className={classes.memoHead}>
            <ButtonBase className={classes.memoToggle} aria-expanded={open} onClick={() => setOpen(!open)}>
              <ExpandMore className={`${classes.chevron} ${open ? '' : classes.collapsed}`} />
              <h2 className={classes.memoTitle}>Credit Memo</h2>
              <HeaderStatus run={run} />
            </ButtonBase>
            {(open || !writeup) && <div className={classes.actions}>{actions()}</div>}
          </div>
          {open && writeup && (
            <div className={classes.body}>
              <MemoContent writeup={writeup} drafts={drafts} onDraft={(index, text) => setDrafts(drafts.map((draft, i) => (i === index ? text : draft)))} />
            </div>
          )}
        </section>
      )}
    </div>
  );
});
